/**
 * Step 6: Export Configuration
 * Summary and download options with file selection
 * When coming from Builder, exports include meshes and full ROS package structure
 */

import { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Title,
  Text,
  Stack,
  Paper,
  Button,
  Group,
  Tabs,
  Code,
  Alert,
  Badge,
  Divider,
  LoadingOverlay,
  Checkbox,
  SimpleGrid,
  TextInput,
  Select,
  CopyButton,
  Radio,
  Progress,
  Loader,
} from "@mantine/core";
import {
  IconFileZip,
  IconAlertCircle,
  IconRobot,
  IconSettings,
  IconCpu,
  IconBrain,
  IconPackage,
  IconTerminal2,
  IconCopy,
  IconCube,
} from "@tabler/icons-react";
import JSZip from "jszip";
import { useConfigureStore } from "../ConfigureStore";
import { CONVERTIBLE_MESH_FORMAT_OPTIONS } from "../../../types";
import type {
  ExportUrdfResultMessage,
  ExportUrdfProgressMessage,
  MeshFormat,
} from "../../../types";

interface ExportStepProps {
  /** True when coming from Builder (with WebSocket for full export) */
  fromBuilder?: boolean;
  /** WebSocket functions for Builder mode */
  wsExport?: {
    sendExportUrdf: (
      packageName: string,
      visualMeshFormat?: MeshFormat,
      collisionMeshFormat?: MeshFormat
    ) => void;
    setOnExportUrdfResult: (callback: ((result: ExportUrdfResultMessage) => void) | null) => void;
    setOnExportUrdfProgress?: (
      callback: ((progress: ExportUrdfProgressMessage) => void) | null
    ) => void;
  };
}

/** Human-readable names for the stages the server reports. */
const EXPORT_STAGE_LABELS: Record<string, string> = {
  building: "Building URDF",
  exporting: "Writing package",
  converting: "Converting meshes",
  packaging: "Packaging",
  postprocessing: "Post-processing",
  encoding: "Preparing download",
};

const LAUNCH_COMMANDS = [
  { value: "gazebo_moveit", label: "MoveIt Demo (Planning)" },
  { value: "gazebo", label: "Gazebo Simulation + RViz" },
  { value: "genesis", label: "Genesis Simulator (No ROS)" },
  { value: "ros1_display", label: "ROS1: RViz Display (Noetic / ROS-O)" },
];

export function ExportStep({ fromBuilder = false, wsExport }: ExportStepProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [activePreview, setActivePreview] = useState<string | null>("servo");
  const [launchCommand, setLaunchCommand] = useState("gazebo_moveit");
  const [customPort, setCustomPort] = useState("");
  // GLB means "ship the meshes as stored". Converting is opt-in, and the
  // server rejects converting only one of the two geometries.
  const [convertMeshes, setConvertMeshes] = useState(false);
  const [visualMeshFormat, setVisualMeshFormat] = useState<MeshFormat>("dae");
  const [collisionMeshFormat, setCollisionMeshFormat] = useState<MeshFormat>("stl");
  const [exportProgress, setExportProgress] = useState<ExportUrdfProgressMessage | null>(null);

  // Track if we're currently processing an export to prevent duplicate processing
  const isProcessingExportRef = useRef(false);

  // Use refs to store current config generation functions for the callback
  const configGeneratorsRef = useRef<{
    generateServoYaml: () => string;
    generateSRDF: () => string;
    generateControllersYaml: () => string;
    generateGazeboYaml: () => string;
    generateILYaml: () => string;
    exportOptions: typeof exportOptions;
    robotName: string;
  } | null>(null);

  const {
    urdfContent,
    packageName,
    setPackageName,
    joints,
    links,
    servoMappings,
    planningGroups,
    controllers,
    disabledCollisionPairs,
    gazeboPhysics,
    gazeboPlugins,
    ilObservation,
    ilAction,
    exportOptions,
    updateExportOptions,
  } = useConfigureStore();

  const actuatedJoints = joints.filter((j) => j.type !== "fixed");
  const robotName = packageName || "robot";

  // Check if at least one export option is selected
  const hasSelectedOptions =
    exportOptions.includeUrdf ||
    exportOptions.includeServoMapping ||
    exportOptions.includeMoveIt ||
    exportOptions.includeGazebo ||
    exportOptions.includeImitationLearning;

  const generateServoYaml = useCallback(() => {
    const mappings = servoMappings.filter((m) => m.servoId !== null);
    return `# Servo Mapping Configuration
# Generated by Robot Compiler

servo_mapping:
${mappings
  .map(
    (m) => `  ${m.jointName}:
    servo_id: ${m.servoId}
    direction: ${m.direction}
    angle_offset: ${m.angleOffset}`
  )
  .join("\n")}`;
  }, [servoMappings]);

  const generateSRDF = useCallback(() => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<!-- SRDF Generated by Robot Compiler -->
<robot name="robot">
${planningGroups
  .map(
    (g) => `  <group name="${g.name}">
${g.joints.map((j) => `    <joint name="${j}" />`).join("\n")}
  </group>`
  )
  .join("\n")}

${planningGroups
  .filter((g) => g.endEffectorLink)
  .map(
    (g) => `  <end_effector name="${g.name}_eef" parent_link="${g.endEffectorLink}" group="${g.name}" />`
  )
  .join("\n")}

  <!-- Disabled Collisions -->
${disabledCollisionPairs
  .map(([l1, l2]) => `  <disable_collisions link1="${l1}" link2="${l2}" reason="User defined" />`)
  .join("\n")}
</robot>`;
  }, [planningGroups, disabledCollisionPairs]);

  const generateControllersYaml = useCallback(() => {
    return `# ros2_control Configuration
# Generated by Robot Compiler

controller_manager:
  ros__parameters:
    update_rate: 100

${controllers
  .map(
    (c) => `${c.name}:
  ros__parameters:
    type: ${c.type === "joint_trajectory_controller" ? "joint_trajectory_controller/JointTrajectoryController" : c.type === "gripper_controller" ? "gripper_action_controller/GripperActionController" : "position_controllers/JointGroupPositionController"}
    joints:
${c.joints.map((j) => `      - ${j}`).join("\n")}`
  )
  .join("\n\n")}`;
  }, [controllers]);

  const generateGazeboYaml = useCallback(() => {
    const enabledPlugins = gazeboPlugins.filter((p) => p.enabled);
    return `# Gazebo Configuration
# Generated by Robot Compiler

physics:
  solver: ${gazeboPhysics.solver}
  step_size: ${gazeboPhysics.stepSize}
  real_time_factor: ${gazeboPhysics.realTimeFactor}

plugins:
${enabledPlugins
  .map(
    (p) => `  - name: ${p.name}
    filename: ${p.filename}${
      Object.keys(p.parameters).length > 0
        ? `
    parameters:
${Object.entries(p.parameters)
  .map(([k, v]) => `      ${k}: ${v}`)
  .join("\n")}`
        : ""
    }`
  )
  .join("\n")}`;
  }, [gazeboPhysics, gazeboPlugins]);

  const generateILYaml = useCallback(() => {
    return `# Imitation Learning Configuration
# Generated by Robot Compiler

observation:
  include_joint_positions: ${ilObservation.includeJointPositions}
  include_joint_velocities: ${ilObservation.includeJointVelocities}
  include_end_effector_pose: ${ilObservation.includeEndEffectorPose}
${
  ilObservation.customObservations.length > 0
    ? `  custom_observations:
${ilObservation.customObservations.map((o) => `    - ${o}`).join("\n")}`
    : ""
}

action:
  type: ${ilAction.actionType}
  scale: ${ilAction.actionScale}
  joints:
${ilAction.selectedJoints.map((j) => `    - ${j}`).join("\n")}`;
  }, [ilObservation, ilAction]);

  // Update ref with current config generators
  useEffect(() => {
    configGeneratorsRef.current = {
      generateServoYaml,
      generateSRDF,
      generateControllersYaml,
      generateGazeboYaml,
      generateILYaml,
      exportOptions,
      robotName,
    };
  }, [generateServoYaml, generateSRDF, generateControllersYaml, generateGazeboYaml, generateILYaml, exportOptions, robotName]);

  // Set up WebSocket callback for export result (Builder mode)
  useEffect(() => {
    console.log("[ExportStep] useEffect running, wsExport:", !!wsExport, "fromBuilder:", fromBuilder);

    if (!wsExport || !fromBuilder) {
      console.log("[ExportStep] Skipping callback setup");
      return;
    }

    console.log("[ExportStep] Setting up export callback");

    const handleExportResult = async (result: ExportUrdfResultMessage) => {
      console.log("[ExportStep] Received export result:", result.success, result.filename, "isProcessing:", isProcessingExportRef.current);

      // Prevent duplicate processing
      if (isProcessingExportRef.current) {
        console.log("[ExportStep] Already processing, ignoring duplicate");
        return;
      }

      if (!result.success || !result.data) {
        setExportError(result.error || "Failed to export URDF package");
        setIsExporting(false);
        return;
      }

      isProcessingExportRef.current = true;

      try {
        // Decode the base64 ZIP from server
        const byteCharacters = atob(result.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Load the ZIP file
        const zip = await JSZip.loadAsync(byteArray);

        console.log("[ExportStep] Loaded ZIP, files:", Object.keys(zip.files));

        // Get current config generators from ref
        const generators = configGeneratorsRef.current;
        if (!generators) {
          throw new Error("Config generators not available");
        }

        const { exportOptions: opts, robotName: name } = generators;
        console.log("[ExportStep] Adding config files for:", name, "options:", opts);

        // Add config files to config/ directory
        if (opts.includeServoMapping) {
          const content = generators.generateServoYaml();
          zip.file(`${name}/config/servo_mapping.yaml`, content);
          console.log("[ExportStep] Added servo_mapping.yaml");
        }
        if (opts.includeMoveIt) {
          zip.file(`${name}/config/${name}.srdf`, generators.generateSRDF());
          zip.file(`${name}/config/controllers.yaml`, generators.generateControllersYaml());
          console.log("[ExportStep] Added MoveIt files");
        }
        if (opts.includeGazebo) {
          zip.file(`${name}/config/gazebo_config.yaml`, generators.generateGazeboYaml());
          console.log("[ExportStep] Added gazebo_config.yaml");
        }
        if (opts.includeImitationLearning) {
          zip.file(`${name}/config/il_config.yaml`, generators.generateILYaml());
          console.log("[ExportStep] Added il_config.yaml");
        }

        console.log("[ExportStep] Final ZIP files:", Object.keys(zip.files));

        // Generate the combined ZIP
        const blob = await zip.generateAsync({ type: "blob" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || `${name}_package.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log("[ExportStep] Download triggered:", a.download);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        console.error("[ExportStep] Error processing export:", error);
        setExportError(`Failed to process export: ${message}`);
      } finally {
        setIsExporting(false);
        isProcessingExportRef.current = false;
      }
    };

    console.log("[ExportStep] Calling setOnExportUrdfResult with callback");
    wsExport.setOnExportUrdfResult(handleExportResult);
    wsExport.setOnExportUrdfProgress?.(setExportProgress);

    return () => {
      console.log("[ExportStep] Cleanup - clearing callback");
      wsExport.setOnExportUrdfResult(null);
      wsExport.setOnExportUrdfProgress?.(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromBuilder]); // wsExport functions are stable, only depend on fromBuilder

  // Export handler for Builder mode (with meshes)
  const handleExportWithMeshes = useCallback(() => {
    if (!wsExport) return;

    const visual = convertMeshes ? visualMeshFormat : "glb";
    const collision = convertMeshes ? collisionMeshFormat : "glb";
    console.log(
      "[ExportStep] Starting export with meshes, robotName:",
      robotName,
      "visual:",
      visual,
      "collision:",
      collision
    );
    setIsExporting(true);
    setExportError(null);
    setExportProgress(null);
    isProcessingExportRef.current = false; // Reset the flag for new export

    // Request URDF package from server - callback will handle adding configs
    wsExport.sendExportUrdf(robotName, visual, collision);
  }, [wsExport, robotName, convertMeshes, visualMeshFormat, collisionMeshFormat]);

  // Export handler for standalone mode (configs only)
  const handleExportConfigsOnly = useCallback(async () => {
    setIsExporting(true);
    setExportError(null);

    try {
      const zip = new JSZip();

      // Add URDF file
      if (exportOptions.includeUrdf && urdfContent) {
        zip.file(`${robotName}.urdf`, urdfContent);
      }

      // Add servo mapping
      if (exportOptions.includeServoMapping) {
        zip.file("config/servo_mapping.yaml", generateServoYaml());
      }

      // Add MoveIt2 files
      if (exportOptions.includeMoveIt) {
        zip.file(`config/${robotName}.srdf`, generateSRDF());
        zip.file("config/controllers.yaml", generateControllersYaml());
      }

      // Add Gazebo config
      if (exportOptions.includeGazebo) {
        zip.file("config/gazebo_config.yaml", generateGazeboYaml());
      }

      // Add IL config
      if (exportOptions.includeImitationLearning) {
        zip.file("config/il_config.yaml", generateILYaml());
      }

      // Generate and download ZIP
      const blob = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${robotName}_config.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setExportError(message);
    } finally {
      setIsExporting(false);
    }
  }, [urdfContent, robotName, exportOptions, generateServoYaml, generateSRDF, generateControllersYaml, generateGazeboYaml, generateILYaml]);

  // Choose export handler based on mode
  const handleExport = fromBuilder && wsExport ? handleExportWithMeshes : handleExportConfigsOnly;

  // A stage reports total=0 when it cannot say how long it is; those show
  // an indeterminate bar rather than a made-up number.
  const progressPercent =
    exportProgress && exportProgress.total > 0
      ? Math.min(100, Math.round((exportProgress.current / exportProgress.total) * 100))
      : null;

  const exportStageLabel = exportProgress
    ? EXPORT_STAGE_LABELS[exportProgress.stage] ?? exportProgress.stage
    : null;

  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={isExporting}
        zIndex={1000}
        loaderProps={{
          children: (
            <Stack gap="xs" align="center" w={280}>
              <Loader size="sm" />
              <Text size="sm" fw={500}>
                {exportProgress?.message ?? "Preparing the export"}
              </Text>
              {/* The server reports a real position only for stages whose
                  length it knows; the rest animate rather than claim one. */}
              <Progress
                value={progressPercent ?? 100}
                animated={progressPercent === null}
                striped={progressPercent === null}
                w="100%"
                size="sm"
              />
              {exportStageLabel && (
                <Text size="xs" c="dimmed">
                  {exportStageLabel}
                </Text>
              )}
            </Stack>
          ),
        }}
      />

      <Stack gap="lg">
        <Box>
          <Title order={3} mb="xs">
            Export Configuration
          </Title>
          <Text c="dimmed" size="sm">
            {fromBuilder
              ? "Export complete ROS package with URDF, meshes, and configuration files."
              : "Select which configuration files to include in your export."}
          </Text>
        </Box>

        {/* Package Name */}
        <Paper withBorder p="md">
          <TextInput
            label="Package Name"
            description="Name of the ROS package (used for directory and file names)"
            placeholder="my_robot"
            leftSection={<IconPackage size={16} />}
            value={packageName}
            onChange={(e) => setPackageName(e.currentTarget.value)}
            styles={{ input: { fontFamily: "monospace" } }}
          />
        </Paper>

        {/* Mesh Format - only the Builder export ships mesh files */}
        {fromBuilder && (
          <Paper withBorder p="md">
            <Group gap="xs" mb="sm">
              <IconCube size={16} />
              <Title order={5}>Mesh Format</Title>
            </Group>
            <Radio.Group
              value={convertMeshes ? "convert" : "keep"}
              onChange={(value) => setConvertMeshes(value === "convert")}
            >
              <Stack gap="xs">
                <Radio
                  value="keep"
                  label="Keep as-is (GLB, smallest)"
                  description="Meshes as stored. Stock RViz and Gazebo cannot load GLB."
                />
                <Radio
                  value="convert"
                  label="Convert for RViz / Gazebo"
                  description="Roughly 10-20x larger than GLB, depending on the formats."
                />
              </Stack>
            </Radio.Group>
            {convertMeshes && (
              <SimpleGrid cols={2} spacing="md" mt="md">
                <Select
                  label="Visual meshes"
                  description={
                    CONVERTIBLE_MESH_FORMAT_OPTIONS.find((o) => o.value === visualMeshFormat)
                      ?.description
                  }
                  data={CONVERTIBLE_MESH_FORMAT_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  value={visualMeshFormat}
                  onChange={(value) => value && setVisualMeshFormat(value as MeshFormat)}
                  allowDeselect={false}
                />
                <Select
                  label="Collision meshes"
                  description={
                    CONVERTIBLE_MESH_FORMAT_OPTIONS.find((o) => o.value === collisionMeshFormat)
                      ?.description
                  }
                  data={CONVERTIBLE_MESH_FORMAT_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  value={collisionMeshFormat}
                  onChange={(value) => value && setCollisionMeshFormat(value as MeshFormat)}
                  allowDeselect={false}
                />
              </SimpleGrid>
            )}
          </Paper>
        )}

        {/* Export Options */}
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Select Files to Export
          </Title>
          <SimpleGrid cols={2} spacing="md">
            <Checkbox
              label={
                <Group gap="xs">
                  <IconRobot size={16} />
                  <Text size="sm">URDF {fromBuilder ? "& Meshes" : "(Original)"}</Text>
                </Group>
              }
              description={fromBuilder ? "Include URDF and all mesh files" : "Include the original URDF file"}
              checked={exportOptions.includeUrdf}
              onChange={(e) =>
                updateExportOptions({ includeUrdf: e.currentTarget.checked })
              }
              disabled={fromBuilder} // Always include URDF in Builder mode
            />

            <Checkbox
              label={
                <Group gap="xs">
                  <IconCpu size={16} />
                  <Text size="sm">Servo Mapping</Text>
                </Group>
              }
              description="Joint to servo ID mapping (servo_mapping.yaml)"
              checked={exportOptions.includeServoMapping}
              onChange={(e) =>
                updateExportOptions({ includeServoMapping: e.currentTarget.checked })
              }
            />

            <Checkbox
              label={
                <Group gap="xs">
                  <IconSettings size={16} />
                  <Text size="sm">MoveIt2</Text>
                </Group>
              }
              description="SRDF, controllers.yaml for MoveIt2"
              checked={exportOptions.includeMoveIt}
              onChange={(e) =>
                updateExportOptions({ includeMoveIt: e.currentTarget.checked })
              }
            />

            <Checkbox
              label={
                <Group gap="xs">
                  <IconRobot size={16} />
                  <Text size="sm">Gazebo</Text>
                </Group>
              }
              description="Physics settings, ros2_control plugin config"
              checked={exportOptions.includeGazebo}
              onChange={(e) =>
                updateExportOptions({ includeGazebo: e.currentTarget.checked })
              }
            />

            <Checkbox
              label={
                <Group gap="xs">
                  <IconBrain size={16} />
                  <Text size="sm">Imitation Learning</Text>
                </Group>
              }
              description="Observation/action space config (il_config.yaml)"
              checked={exportOptions.includeImitationLearning}
              onChange={(e) =>
                updateExportOptions({ includeImitationLearning: e.currentTarget.checked })
              }
            />
          </SimpleGrid>
        </Paper>

        {/* Summary */}
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Configuration Summary
          </Title>
          <Group gap="xl">
            <Stack gap="xs">
              <Text size="sm" c="dimmed">URDF</Text>
              <Badge size="lg" variant="light">
                {joints.length} joints, {links.length} links
              </Badge>
            </Stack>
            {exportOptions.includeServoMapping && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">Servo Mapping</Text>
                <Badge size="lg" variant="light">
                  {servoMappings.filter((m) => m.servoId !== null).length} / {actuatedJoints.length} configured
                </Badge>
              </Stack>
            )}
            {exportOptions.includeMoveIt && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">MoveIt2</Text>
                <Badge size="lg" variant="light">
                  {planningGroups.length} groups, {controllers.length} controllers
                </Badge>
              </Stack>
            )}
            {exportOptions.includeGazebo && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">Gazebo</Text>
                <Badge size="lg" variant="light">
                  {gazeboPlugins.filter((p) => p.enabled).length} plugins
                </Badge>
              </Stack>
            )}
          </Group>
        </Paper>

        {/* Export Button */}
        <Group>
          <Button
            size="lg"
            leftSection={<IconFileZip size={20} />}
            onClick={handleExport}
            disabled={!hasSelectedOptions && !fromBuilder}
          >
            {fromBuilder ? "Download ROS Package (ZIP)" : "Download Selected Configs (ZIP)"}
          </Button>
          {!hasSelectedOptions && !fromBuilder && (
            <Text size="sm" c="red">
              Please select at least one file type to export
            </Text>
          )}
        </Group>

        {/* Launch Command (curl | bash) */}
        {fromBuilder && (
          <Paper withBorder p="md">
            <Title order={5} mb="md">
              <Group gap="xs">
                <IconTerminal2 size={18} />
                Launch Command (curl | bash)
              </Group>
            </Title>
            <Text size="sm" c="dimmed" mb="md">
              Copy a one-line command to build and launch this robot on any ROS 2 machine.
            </Text>
            <Group align="end" mb="md">
              <Select
                label="Launch Type"
                data={LAUNCH_COMMANDS}
                value={launchCommand}
                onChange={(val) => val && setLaunchCommand(val)}
                style={{ flex: 1 }}
              />
              <TextInput
                label="Port (optional)"
                description="Override port for remote access"
                placeholder={window.location.port || "80"}
                value={customPort}
                onChange={(e) => setCustomPort(e.currentTarget.value.replace(/\D/g, ""))}
                style={{ width: 160 }}
                styles={{ input: { fontFamily: "monospace" } }}
              />
            </Group>
            {(() => {
              const origin = customPort
                ? `${window.location.protocol}//${window.location.hostname}:${customPort}`
                : window.location.origin;
              const portParam = customPort ? `&port=${customPort}` : "";
              const sessionParam = window.sessionId ? `&session=${window.sessionId}` : "";
              // ROS1 launcher is a stand-alone Python script (curl ... | python3 -).
              // All other launchers are shell scripts.
              const isPythonLauncher = launchCommand.startsWith("ros1_");
              const pipeTarget = isPythonLauncher ? "python3 -" : "bash";
              const curlCmd = `curl -s -L "${origin}/api/launch-it?package_name=${encodeURIComponent(robotName)}&launch_command=${launchCommand}${portParam}${sessionParam}" | ${pipeTarget}`;
              return (
                <>
            <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", position: "relative" }}>
              {curlCmd}
            </Code>
            <Group mt="sm">
              <CopyButton
                value={curlCmd}
              >
                {({ copied, copy }) => (
                  <Button
                    variant={copied ? "filled" : "light"}
                    color={copied ? "teal" : "blue"}
                    leftSection={<IconCopy size={16} />}
                    onClick={() => {
                      // Mantine's copy uses navigator.clipboard which
                      // fails on non-HTTPS origins.  Fall back to the
                      // textarea + execCommand trick.
                      try {
                        copy();
                      } catch {
                        const ta = document.createElement("textarea");
                        ta.value = curlCmd;
                        ta.style.position = "fixed";
                        ta.style.left = "-9999px";
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                    }}
                  >
                    {copied ? "Copied!" : "Copy Launch Command"}
                  </Button>
                )}
              </CopyButton>
            </Group>
                </>
              );
            })()}
          </Paper>
        )}

        {exportError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Export Error"
            color="red"
            variant="light"
          >
            {exportError}
          </Alert>
        )}

        <Divider label="File Previews" labelPosition="center" />

        {/* File Previews */}
        <Tabs value={activePreview} onChange={setActivePreview}>
          <Tabs.List>
            {exportOptions.includeServoMapping && (
              <Tabs.Tab value="servo">servo_mapping.yaml</Tabs.Tab>
            )}
            {exportOptions.includeMoveIt && (
              <>
                <Tabs.Tab value="srdf">robot.srdf</Tabs.Tab>
                <Tabs.Tab value="controllers">controllers.yaml</Tabs.Tab>
              </>
            )}
          </Tabs.List>

          {exportOptions.includeServoMapping && (
            <Tabs.Panel value="servo" pt="md">
              <Paper withBorder p="md" bg="gray.0">
                <Code block style={{ whiteSpace: "pre-wrap", maxHeight: 400, overflow: "auto" }}>
                  {generateServoYaml()}
                </Code>
              </Paper>
            </Tabs.Panel>
          )}

          {exportOptions.includeMoveIt && (
            <>
              <Tabs.Panel value="srdf" pt="md">
                <Paper withBorder p="md" bg="gray.0">
                  <Code block style={{ whiteSpace: "pre-wrap", maxHeight: 400, overflow: "auto" }}>
                    {generateSRDF()}
                  </Code>
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="controllers" pt="md">
                <Paper withBorder p="md" bg="gray.0">
                  <Code block style={{ whiteSpace: "pre-wrap", maxHeight: 400, overflow: "auto" }}>
                    {generateControllersYaml()}
                  </Code>
                </Paper>
              </Tabs.Panel>
            </>
          )}
        </Tabs>
      </Stack>
    </Box>
  );
}
